import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';
import { colors, spacing, typography, shadows } from '../../design-system';

export const chartPalette = {
  primary: colors.primary[500],
  secondary: colors.secondary[500],
  success: colors.success.main,
  warning: colors.warning.main,
  error: colors.error.main,
  info: colors.info.main,
  // NOTE: this is a static, non-theme-aware fallback palette (module scope,
  // no React context). Real chart rendering should use useChartTheme()'s
  // `palette` object below instead, which reads live theme.palette values and
  // adapts correctly to dark mode.
  text: colors.text.primary,
  textMuted: colors.text.secondary,
  grid: alpha(colors.text.secondary, 0.12),
  axis: alpha(colors.text.secondary, 0.4),
  cursor: alpha(colors.primary[500], 0.14),
  surface: "background.paper",
  surfaceAlt: "background.default",
};

export const chartSeriesPalette = [
  chartPalette.primary,
  chartPalette.secondary,
  chartPalette.success,
  chartPalette.warning,
  chartPalette.error,
  chartPalette.info,
];

export const chartAnimation = {
  duration: 900,
  begin: 80,
  stagger: 120,
  easing: 'ease-out',
};

export const formatCompactNumber = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return value;
  }

  return new Intl.NumberFormat('es-ES', {
    maximumFractionDigits: value % 1 === 0 ? 0 : 1,
  }).format(value);
};

export const getSeriesColor = (index, providedColor) => {
  if (providedColor) {
    return providedColor;
  }

  return chartSeriesPalette[index % chartSeriesPalette.length];
};

export const toSafeId = (value) => String(value).replace(/[^a-zA-Z0-9_-]/g, '-');

export const normalizeDataKeys = (dataKeys = [], data = [], excludedKeys = ['name']) => {
  if (Array.isArray(dataKeys) && dataKeys.length > 0) {
    return dataKeys.map((item, index) => {
      if (typeof item === 'string') {
        return {
          dataKey: item,
          name: item,
          color: getSeriesColor(index),
        };
      }

      return {
        ...item,
        dataKey: item.dataKey,
        name: item.name || item.dataKey,
        color: getSeriesColor(index, item.color),
      };
    });
  }

  const sample = data.find((entry) => entry && typeof entry === 'object') || {};

  return Object.keys(sample)
    .filter((key) => !excludedKeys.includes(key) && typeof sample[key] === 'number')
    .map((key, index) => ({
      dataKey: key,
      name: key,
      color: getSeriesColor(index),
    }));
};

export const buildLegendItems = (items = []) => items.map((item, index) => ({
  dataKey: item.dataKey || item.name || `series-${index}`,
  name: item.name || item.dataKey || `Serie ${index + 1}`,
  color: item.color || getSeriesColor(index),
}));

export const useChartTheme = (height = 320) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  return {
    height,
    isMobile,
    isTablet,
    titleSize: isMobile ? typography.fontSize.base : typography.fontSize.lg,
    subtitleSize: isMobile ? typography.fontSize.xs : typography.fontSize.sm,
    axisFontSize: isMobile ? 10 : 12,
    legendFontSize: isMobile ? 11 : 12,
    tooltipTitleSize: isMobile ? typography.fontSize.sm : typography.fontSize.base,
    tooltipValueSize: typography.fontSize.xs,
    tickCount: isMobile ? 4 : isTablet ? 5 : 7,
    yTickCount: isMobile ? 4 : 6,
    margin: {
      top: 12,
      right: isMobile ? 8 : 16,
      left: isMobile ? 0 : 8,
      bottom: isMobile ? 12 : 8,
    },
    legendHeight: isMobile ? 68 : 48,
    barRadius: isMobile ? 4 : 6,
    barSize: isMobile ? 18 : 26,
    fontFamily: typography.fontFamily.secondary,
    palette: {
      text: theme.palette.text.primary,
      textMuted: theme.palette.text.secondary,
      grid: alpha(theme.palette.text.secondary, 0.14),
      axis: alpha(theme.palette.text.secondary, 0.45),
      cursor: alpha(theme.palette.primary.main, 0.2),
    },
  };
};

export const ChartContainer = ({ title, subtitle, action, children, sx = {} }) => {
  const theme = useTheme();

  return (
    <Box
      data-testid="chart-container"
      sx={{
        width: '100%',
        p: { xs: 1.5, sm: 2 },
        borderRadius: `${spacing.borderRadius.xl}px`,
        backgroundColor: 'background.paper',
        backgroundImage: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.default, 0.92)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.32 : 0.12)}`,
        boxShadow: theme.palette.mode === 'dark' ? '0 10px 28px rgba(0,0,0,0.28)' : shadows.base,
        ...sx,
      }}
    >
      {(title || subtitle || action) && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 2 }}
        >
          <Box>
            {title && (
              <Typography
                sx={{
                  fontFamily: typography.fontFamily.secondary,
                  fontWeight: typography.fontWeight.semibold,
                  fontSize: { xs: typography.fontSize.base, sm: typography.fontSize.lg },
                  color: 'text.primary',
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                sx={{
                  mt: 0.5,
                  color: 'text.secondary',
                  fontSize: { xs: typography.fontSize.xs, sm: typography.fontSize.sm },
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Stack>
      )}
      {children}
    </Box>
  );
};

// SVG gradient URLs (url(#...)) are not valid CSS colors for MUI alpha().
const resolveColor = (c) => (typeof c === 'string' && !c.startsWith('url(') ? c : null);

export const ChartTooltip = ({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter = formatCompactNumber,
  hideLabel = false,
  seriesColors = {},
}) => {
  const theme = useTheme();
  if (!active || !payload || !payload.length) {
    return null;
  }

  const resolvedLabel = labelFormatter ? labelFormatter(label, payload) : label;

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 160,
        px: 1.5,
        py: 1.25,
        borderRadius: `${spacing.borderRadius.lg}px`,
        backgroundColor: alpha(theme.palette.background.paper, 0.98),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
        boxShadow: shadows.lg,
        backdropFilter: 'blur(10px)',
      }}
    >
      {!hideLabel && resolvedLabel !== undefined && resolvedLabel !== null && resolvedLabel !== '' && (
        <Typography
          sx={{
            mb: 1,
            color: 'text.primary',
            fontWeight: typography.fontWeight.semibold,
            fontSize: typography.fontSize.sm,
          }}
        >
          {resolvedLabel}
        </Typography>
      )}

      <Stack spacing={0.75}>
        {payload.map((entry) => (
          <Stack
            key={`${entry.dataKey}-${entry.name}`}
            direction="row"
            justifyContent="space-between"
            spacing={2}
            alignItems="center"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: seriesColors[entry.dataKey] || resolveColor(entry.color) || resolveColor(entry.payload?.fill) || colors.primary[500],
                  boxShadow: `0 0 0 3px ${alpha(seriesColors[entry.dataKey] || resolveColor(entry.color) || resolveColor(entry.payload?.fill) || colors.primary[500], 0.16)}`,
                }}
              />
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: typography.fontSize.xs,
                }}
              >
                {entry.name}
              </Typography>
            </Stack>
            <Typography
              sx={{
                color: 'text.primary',
                fontWeight: typography.fontWeight.semibold,
                fontSize: typography.fontSize.xs,
              }}
            >
              {valueFormatter(entry.value, entry.name, entry)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

export const InteractiveLegend = ({ items = [], hiddenKeys = [], onToggle, isMobile = false }) => {
  if (!items.length) {
    return null;
  }

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      justifyContent="center"
      gap={1}
      sx={{
        mt: 1.5,
        px: isMobile ? 0 : 1,
      }}
    >
      {items.map((item) => {
        const hidden = hiddenKeys.includes(item.dataKey);

        return (
          <Box
            key={item.dataKey}
            component="button"
            type="button"
            onClick={() => onToggle?.(item.dataKey)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              py: 0.75,
              borderRadius: `${spacing.borderRadius.full}px`,
              border: `1px solid ${alpha(item.color, hidden ? 0.18 : 0.3)}`,
              backgroundColor: hidden ? 'background.paper' : alpha(item.color, 0.1),
              color: hidden ? 'text.secondary' : 'text.primary',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: isMobile ? typography.fontSize.xs : typography.fontSize.sm,
              '&:hover': {
                borderColor: alpha(item.color, 0.45),
                transform: 'translateY(-1px)',
              },
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: item.color,
                opacity: hidden ? 0.35 : 1,
              }}
            />
            <Typography
              component="span"
              sx={{
                fontSize: 'inherit',
                fontWeight: hidden ? typography.fontWeight.regular : typography.fontWeight.medium,
              }}
            >
              {item.name}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};

export const ChartEmptyState = ({ message = 'No hay datos disponibles para este gráfico.' }) => (
  <Box
    sx={{
      minHeight: 180,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'text.secondary',
      textAlign: 'center',
      px: 2,
    }}
  >
    <Typography sx={{ fontSize: typography.fontSize.sm }}>{message}</Typography>
  </Box>
);

const chartTheme = {
  chartPalette,
  chartSeriesPalette,
  chartAnimation,
  formatCompactNumber,
  getSeriesColor,
  toSafeId,
  normalizeDataKeys,
  buildLegendItems,
};

export default chartTheme;
